// =============================================================================
// Trial visitors — talks to Logipulse.Trial.Service's back-office endpoints
// (/admin/trial/*). Lists who has visited the tryout and what standing they are
// in, and lets a tester put a blocked one back on the road without opening SSMS.
//
// WHY THIS DOES NOT LOOK LIKE THE OTHER SERVICES HERE.
// pricingConfigService and adminDataService both send a Cognito bearer token to
// the .NET SaaS Tracker. This one talks to a DIFFERENT service — the trial
// service — which has no authentication of its own, because every one of its
// visitor-facing endpoints acts only on the caller's own trial and verifies
// device ownership first.
//
// The admin endpoints are the exception, and they are protected two ways:
//
//   1. The trial service does not ROUTE them unless TrialAdmin:Enabled is true.
//      On a production host the paths 404 because nothing is listening.
//   2. When routed, they require the X-Admin-Key header below.
//
// ⚠ THE KEY IS NOT A SECRET FROM THE USER OF THIS CONSOLE. This is a React
//   application: every REACT_APP_* value is baked into the bundle and readable
//   by anyone who opens devtools. It is a deployment gate for a test host, not a
//   credential — which is why the trial service refuses to enable these
//   endpoints by default and warns loudly at startup when they are on.
//   Do not point this at a production trial service.
// =============================================================================

/** Mirrors Domain/Enums/TrialStatus.cs, which serialises by name. */
export type TrialStatus =
  | 'Active'
  | 'Warning'
  | 'RegistrationRequired'
  | 'Blocked'
  | 'Registered';

/** Mirrors Api/Contracts/TrialAdminVisitorApiResponse. */
export interface TrialAdminVisitor {
  trialId: string;
  status: TrialStatus;
  visitCount: number;
  /** Operating system the client reported, e.g. "Windows". Diagnostics, not identity. */
  platform: string | null;
  browser: string | null;
  browserVersion: string | null;
  /** ISO-8601. */
  firstVisit: string;
  /** ISO-8601. When the CURRENT session began — not when the visitor was last seen. */
  lastVisit: string;
  /** ISO-8601. When the row was last written. This is the last-seen value. */
  updatedOn: string;

  // ── LOCATION: LIST ENDPOINT ONLY, AND OFTEN NULL ───────────────────────────
  //
  // These three do not live on TrialVisitors. They are what the visitor's
  // address geolocated to, stored on TrialDeviceSignals, so filling them in
  // costs a join — which only getVisitors() performs. getBlockedVisitors()
  // leaves them undefined.
  //
  // NULL IS THE ORDINARY CASE, not a failure: the provider can be switched off,
  // can time out or rate-limit, and has nothing to say about a loopback address
  // — which is every visitor on a developer machine. Render them as unknown,
  // never as an empty cell that reads like data.
  //
  // The address itself is hashed the moment it arrives and is stored nowhere.

  /** ISO-3166 alpha-2, or null/absent. */
  countryCode?: string | null;
  countryName?: string | null;
  city?: string | null;
}

/** Mirrors Application/DTOs/TrialAdminUpdateRequest. Send only what should change. */
export interface TrialAdminUpdate {
  visitCount?: number;
  status?: TrialStatus;
  /**
   * Start the trial's calendar again from now.
   *
   * REQUIRED FOR A REOPEN THAT ACTUALLY WORKS. Trial policy blocks on the day
   * window BEFORE it reads any counter, so a trial older than
   * Trial:MaxTrialDays that is reopened with only visitCount and status is
   * re-blocked on the visitor's very next request — the console showed a
   * successful reopen and the visitor stayed locked out. The counter had been
   * reset; the calendar had not.
   */
  resetFirstVisit?: boolean;
}

const API_ROOT = (process.env.REACT_APP_TRIAL_API_URL as string | undefined) ?? '';
const ADMIN_KEY = (process.env.REACT_APP_TRIAL_ADMIN_KEY as string | undefined) ?? '';

/** Mirrors AdminKeyEndpointFilter.HeaderName. */
const ADMIN_KEY_HEADER = 'X-Admin-Key';

/**
 * True when both settings are present. The section hides itself otherwise
 * rather than rendering a table that can only ever show a network error.
 */
export const isTrialAdminConfigured = (): boolean => !!API_ROOT && !!ADMIN_KEY;

function headers(): Record<string, string> {
  return { 'Content-Type': 'application/json', [ADMIN_KEY_HEADER]: ADMIN_KEY };
}

async function req<T>(path: string, method: string, body?: unknown): Promise<T> {
  const res = await fetch(`${API_ROOT.replace(/\/+$/, '')}${path}`, {
    method,
    headers: headers(),
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });

  if (!res.ok) {
    // 404 has two meanings here and the difference is the first thing anyone
    // debugging this needs, so spell it out rather than surfacing a bare code.
    if (res.status === 404 && method === 'GET') {
      throw new Error(
        'The trial service did not serve /admin/trial (404). Either TrialAdmin:Enabled is false on ' +
        'that host, or REACT_APP_TRIAL_ADMIN_KEY does not match its TrialAdmin:ApiKey — the service ' +
        'answers 404 for a wrong key on purpose, so the two look identical from here.',
      );
    }

    // The API returns an RFC 9457 problem document; `detail` is the useful part.
    let detail = '';
    try {
      const problem = JSON.parse(await res.text());
      detail = problem?.detail ? ` — ${problem.detail}` : '';
    } catch {
      /* non-JSON body */
    }
    throw new Error(`${method} ${path} failed (${res.status})${detail}`);
  }

  const text = await res.text();
  return (text ? JSON.parse(text) : null) as T;
}

/**
 * Every visitor the trial has stopped, most recently active first.
 *
 * Superseded for screen purposes by getVisitors(), which returns this list as a
 * subset. Kept because it is a published endpoint and something may still call
 * it; it does NOT carry country or city.
 */
export const getBlockedVisitors = () =>
  req<TrialAdminVisitor[]>('/admin/trial/blocked', 'GET');

/**
 * EVERY visitor, whatever their standing, most recently SEEN first.
 *
 * Ordered by updatedOn rather than lastVisit, because lastVisit is when the
 * visitor's current session began — somebody who has been on the site all
 * afternoon has an old lastVisit and a fresh updatedOn, and belongs at the top
 * of a list an agent is scanning.
 *
 * Capped server-side with no paging. If the list looks truncated it is.
 */
export const getVisitors = () =>
  req<TrialAdminVisitor[]>('/admin/trial/visitors', 'GET');

/**
 * Applies an edit and returns the row as it now stands.
 *
 * Note for callers: lowering visitCount ALONE does not readmit a blocked
 * visitor — TrialPolicy treats Blocked as terminal before it reads any
 * threshold. Send status: 'Active' as well, and — for anything older than
 * Trial:MaxTrialDays — resetFirstVisit too. See reopenVisitor below, which is
 * the call to use rather than assembling the three fields by hand.
 */
export const updateVisitor = (trialId: string, update: TrialAdminUpdate) =>
  req<TrialAdminVisitor>(`/admin/trial/${encodeURIComponent(trialId)}`, 'PATCH', update);

/**
 * Put a visitor back to where a brand-new one starts: no visits spent, Active,
 * and the day window running again from now.
 *
 * ── WHY THIS IS A FUNCTION AND NOT THREE FIELDS AT THE CALL SITE ─────────────
 *
 * "Reopen" is one operation, and it was previously assembled out of a generic
 * PATCH at the point of use — which is exactly how it ended up incomplete. It
 * sent visitCount and status and omitted the calendar, so every trial older
 * than Trial:MaxTrialDays was re-blocked seconds later and the button looked
 * broken for no visible reason. Naming the operation once means the next caller
 * cannot get the combination wrong.
 */
export const reopenVisitor = (trialId: string) =>
  updateVisitor(trialId, { visitCount: 0, status: 'Active', resetFirstVisit: true });
